import { SortDirection } from "@bridgesplit/utils";

import { TokenListMetadata } from "./metadata";
import { PrincipalCollateralPair } from "./arguments";

/** Loans */

// Ordering MUST match the Rust enum. Reordering or inserting variants here
// without the matching Rust change will silently re-route sort requests on the BE.
export enum LoanInfoSorting {
    EndTime,
    StartTime,
    Principal,
    LoanHealth,
    Collateral,
    LeverageMultiplier,
    NetApy,
    NetPositionValue
}

export enum LoanFilterType {
    Active,
    Closed,
    RefinanceEligible,
    TimeBasedLiquidationEligible
}

export interface LoanFilterApi {
    //The loan filter that matches the LoanFilter type on the BE
    loanAddresses: string[];
    lenders: string[];
    borrowers: string[];
    filterType?: number;
    principalMints: string[];
    page?: number;
    pageSize?: number;
    sortSide: SortDirection;
    sortType: LoanInfoSorting;
    orderFundingTypes?: AbfOrderFundingType;
    collateralMints: string[];
    collateralIdentifiers: string[];
    loopVaultPrincipalCollateralPairs?: PrincipalCollateralPair[];
    // When true, BE bundles the per-loan pnl breakdown onto each items[i].pnl —
    // saves the follow-up /loans/pnl roundtrip. The two pipelines run in parallel
    // server-side, so latency is ~max(list, pnl) rather than a sum.
    includePnl?: boolean;
}
export enum LoanStatusType {
    ActiveOnly,
    InactiveOnly,
    All
}

export interface LoanPaginationInfo {
    loanStatusType: LoanStatusType;
    page: number;
    pageSize: number;
    sortSide: SortDirection;
    sortType: LoanInfoSorting;
    principalMint?: string;
    collateralMint?: string;
}

export interface LedgerDebt {
    total: number;
    interestAccrued: number;
}

export interface PastLoanCollateral {
    id?: number;
    usdPrice: number;
    assetType: AssetTypeIdentifier;
    amount: number;
    metadata: TokenListMetadata;
}

export enum AbfOrderFundingType {
    Standard = 0,
    Syndicated = 1, //deprecated
    Loop = 2
}

export enum LoanStatus {
    RepaidLoan = "Repaid",
    OngoingLoan = "Ongoing",
    DefaultedLoan = "Defaulted",
    RefinanceGrace = "Refinancing"
}

export enum AssetTypeIdentifier {
    SplToken = 0, //"HFxmxLSwEbB",
    StakedSol = 1, //"9uBaD2bCVi5",
    OrcaPosition = 2, //"AQBQodNFs6F"
    RaydiumPosition = 4
}

/** Permissioned Loans */
export interface PermissionedLoan {
    id?: number;
    borrower: string;
    principal: string;
    collateral: string;
    nonce: number;
    loanAddress: string;
}

export interface PermissionedLoanFilter {
    borrower: string;
    principal: string;
    collateral: string;
}

export enum TokenStandard {
    Standard = 0,
    PermissionedRWA = 1
}

export enum LoanInfoStatus {
    Ended,
    Active,
    Liquidated,
    Defaulted,
    NotStarted
}

export interface ToggleLoanPositionParams {
    loanAddress: string;
    timestamp: number;
    signature: string;
}

/**
 * Set the borrower's floor on the liquidation threshold a loan may be refinanced into.
 * `minLiquidationThreshold` is in millionths (1_000_000 = 100%); `0` clears the floor.
 * Signature-authenticated, borrower-only.
 */
export interface SetRefinanceConstraintParams {
    loanAddress: string;
    minLiquidationThreshold: number;
    timestamp: number;
    signature: string;
}

/** `minLiquidationThreshold` is `null` when no floor is configured. */
export interface RefinanceConstraintResponse {
    loanAddress: string;
    minLiquidationThreshold: number | null;
}
